import type { SimulationPath } from '../../simulation.types'

/**
 * Sorts paths based on the order of the expressions in the paths. It's a bit of a
 * tricky one to explain, but the idea is that we need to evaluate the paths in the
 * order that the expressions we pass through would evaluate.
 *
 * e.g. if a Switch has three groups:
 * 1. A = foo & B = bar
 * 2. A = foo
 * 3. C = toasty
 *
 * The value A = foo would be true for both 1/2, but we want to progress through the paths
 * where the expression matches 1 as part of simulation first.
 *
 * It's somewhat of an optimisation than strictly necessary, since we're ultimately evaluating
 * expressions which return the branch uuid we need to follow, rather than whether the given group
 * is 'true' or a not. But, still, it's good to only evaluate what we need to evaluate!
 *
 * We also want to account for paths which 'skip' expressions.
 *
 *   A [1][3]     index 0:  A=1  B=1  C=2  -> C last
 *   B [1][1][1]  index 1:  A=3  B=1       -> B before A
 *   C [2][2]     => B, A, C
 *
 * A shorter route that matches the start of a longer one comes first, since it
 * reached an end state without having to take a lower branch on the way.
 */
export const comparePathPriority = (
  a: Pick<SimulationPath, 'expressions'>,
  b: Pick<SimulationPath, 'expressions'>
): number =>
  compareOrders(
    a.expressions.map(expression => expression.order),
    b.expressions.map(expression => expression.order)
  )

const compareOrders = (a: number[], b: number[]): number => {
  const [aHead, ...aRest] = a
  const [bHead, ...bRest] = b

  if (aHead === undefined || bHead === undefined) {
    return a.length - b.length
  }

  return aHead === bHead ? compareOrders(aRest, bRest) : aHead - bHead
}
